import { useEffect, useMemo, useState } from 'react';
import { ClipboardCopy, Loader, Package, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const blankItem = () => ({
  stock_item: '',
  quantity: '1',
  serial_or_mac: '',
  ownership: 'user',
  notes: '',
});

const apiError = (error) => {
  const data = error.response?.data;
  if (typeof data?.detail === 'string') return data.detail;
  if (typeof data?.requested_username?.[0] === 'string') return data.requested_username[0];
  if (typeof data?.items?.[0] === 'string') return data.items[0];
  return 'Could not save the installation request.';
};

const InstallationRequests = () => {
  const [user, setUser] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ requested_username: '', notes: '', items: [blankItem()] });

  const canSubmit = useMemo(
    () => ['officer', 'technician', 'officer_technician'].includes(user?.role),
    [user],
  );
  const isReviewer = user?.role === 'admin' || user?.role === 'manager';

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [userRes, stockRes, requestRes] = await Promise.all([
        api.get('/users/me/'),
        api.get('/stock-items/inventory/'),
        api.get('/installation-invoice-requests/'),
      ]);
      setUser(userRes.data);
      setInventory(stockRes.data.results || stockRes.data || []);
      setRequests(requestRes.data.results || requestRes.data || []);
    } catch {
      setError('Could not load installation requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial API hydration for this page.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadData();
  }, []);

  const setItem = (index, field, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [field]: value } : item
      )),
    }));
  };

  const removeItem = (index) => {
    setForm((current) => ({
      ...current,
      items: current.items.length === 1
        ? current.items
        : current.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/installation-invoice-requests/', {
        requested_username: form.requested_username.trim(),
        notes: form.notes.trim(),
        items: form.items.map((item) => ({
          ...item,
          stock_item: Number(item.stock_item),
          quantity: Number(item.quantity),
        })),
      });
      setForm({ requested_username: '', notes: '', items: [blankItem()] });
      setSuccess('Installation information sent to the admin.');
      const requestRes = await api.get('/installation-invoice-requests/');
      setRequests(requestRes.data.results || requestRes.data || []);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteRequest = async (request) => {
    const message = isReviewer
      ? `Remove the request for ${request.requested_username}?`
      : `Cancel your request for ${request.requested_username}?`;
    if (!window.confirm(message)) return;
    try {
      await api.delete(`/installation-invoice-requests/${request.id}/`);
      setRequests((current) => current.filter((entry) => entry.id !== request.id));
    } catch {
      setError('Could not remove the request.');
    }
  };

  const copyRequest = async (request) => {
    const lines = [
      `Customer username: ${request.requested_username}`,
      ...request.items.map((item) => {
        const serial = item.serial_or_mac ? ` | MAC/serial: ${item.serial_or_mac}` : '';
        const note = item.notes ? ` | ${item.notes}` : '';
        return `${item.stock_item_name}: ${item.quantity} ${item.stock_item_unit} | Owner: ${item.ownership}${serial}${note}`;
      }),
      request.notes ? `General notes: ${request.notes}` : '',
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setSuccess(`Details for ${request.requested_username} copied.`);
    } catch {
      setError('Clipboard access was blocked by the browser.');
    }
  };

  const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100';

  if (loading) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader className="animate-spin text-purple-600" /></div>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Installation Invoice Requests</h1>
          <p className="mt-1 text-sm text-gray-600">
            {isReviewer
              ? 'Temporary field details waiting to be entered in the existing New Installation form.'
              : 'Send the exact material usage for a new customer installation.'}
          </p>
        </div>
        <button onClick={loadData} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{success}</div>}

      {canSubmit && (
        <form onSubmit={submitRequest} className="space-y-5 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Request an installation invoice</h2>
            <p className="text-sm text-gray-500">The customer must not have been created in the CRM yet.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">New customer username *</label>
              <input
                required
                value={form.requested_username}
                onChange={(event) => setForm((current) => ({ ...current, requested_username: event.target.value }))}
                className={inputClass}
                placeholder="e.g. 5city009"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">General notes</label>
              <input
                value={form.notes}
                onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                className={inputClass}
                placeholder="Optional installation notes"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Materials used</h3>
              <button type="button" onClick={() => setForm((current) => ({ ...current, items: [...current.items, blankItem()] }))} className="inline-flex items-center gap-1 text-sm font-medium text-purple-700 hover:text-purple-900">
                <Plus size={16} /> Add item
              </button>
            </div>
            {form.items.map((item, index) => {
              const selected = inventory.find((stock) => stock.id === Number(item.stock_item));
              return (
                <div key={index} className="grid gap-3 rounded-lg bg-gray-50 p-3 md:grid-cols-12">
                  <div className="md:col-span-4">
                    <label className="mb-1 block text-xs font-medium text-gray-600">Stock item *</label>
                    <select required value={item.stock_item} onChange={(event) => setItem(index, 'stock_item', event.target.value)} className={inputClass}>
                      <option value="">Select from stock</option>
                      {inventory.map((stock) => (
                        <option key={stock.id} value={stock.id}>
                          {stock.name} ({stock.current_stock} {stock.unit} available)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-gray-600">Quantity {selected ? `(${selected.unit})` : ''} *</label>
                    <input required type="number" min="1" step="1" value={item.quantity} onChange={(event) => setItem(index, 'quantity', event.target.value)} className={inputClass} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-gray-600">Ownership *</label>
                    <select value={item.ownership} onChange={(event) => setItem(index, 'ownership', event.target.value)} className={inputClass}>
                      <option value="user">User</option>
                      <option value="company">Company</option>
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <label className="mb-1 block text-xs font-medium text-gray-600">MAC / serial / note</label>
                    <input value={item.serial_or_mac} onChange={(event) => setItem(index, 'serial_or_mac', event.target.value)} className={inputClass} placeholder="Modem MAC or serial" />
                    <input value={item.notes} onChange={(event) => setItem(index, 'notes', event.target.value)} className={`${inputClass} mt-2`} placeholder="Optional item note" />
                  </div>
                  <div className="flex items-end md:col-span-1">
                    <button type="button" disabled={form.items.length === 1} onClick={() => removeItem(index)} className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-30" aria-label="Remove item">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end">
            <button disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2.5 font-medium text-white hover:bg-purple-700 disabled:opacity-50">
              {saving && <Loader size={16} className="animate-spin" />} Send to admin
            </button>
          </div>
        </form>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{isReviewer ? 'Waiting requests' : 'My waiting requests'}</h2>
          <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700">{requests.length}</span>
        </div>

        {requests.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500">
            <Package className="mx-auto mb-3 text-gray-400" /> No installation requests are waiting.
          </div>
        ) : requests.map((request) => (
          <article key={request.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{request.requested_username}</h3>
                <p className="text-sm text-gray-500">
                  Requested by {request.requested_by_name} · {new Date(request.requested_at).toLocaleString()}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {isReviewer && (
                  <button onClick={() => copyRequest(request)} className="inline-flex items-center gap-2 rounded-lg border border-purple-200 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50">
                    <ClipboardCopy size={16} /> Copy details
                  </button>
                )}
                <button onClick={() => deleteRequest(request)} className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">
                  <Trash2 size={16} /> {isReviewer ? 'Remove' : 'Cancel'}
                </button>
              </div>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                  <tr><th className="px-3 py-2">Stock item</th><th className="px-3 py-2">Used</th><th className="px-3 py-2">Owner</th><th className="px-3 py-2">MAC / serial</th><th className="px-3 py-2">Notes</th></tr>
                </thead>
                <tbody className="divide-y">
                  {request.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-2 font-medium text-gray-900">{item.stock_item_name}</td>
                      <td className="px-3 py-2">{item.quantity} {item.stock_item_unit}</td>
                      <td className="px-3 py-2 capitalize">{item.ownership}</td>
                      <td className="px-3 py-2">{item.serial_or_mac || '—'}</td>
                      <td className="px-3 py-2">{item.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {request.notes && <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900"><strong>Notes:</strong> {request.notes}</p>}
            {isReviewer && (
              <p className="mt-4 text-sm text-gray-600">
                Use the existing <Link to="/customers" className="font-semibold text-purple-700 hover:underline">New Installation</Link> form. This request disappears automatically after its invoice is created.
              </p>
            )}
          </article>
        ))}
      </section>
    </div>
  );
};

export default InstallationRequests;
